import React from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setTabs } from '../redux/slices/tabsDataSlice';
import { setOthers, setAuthorCardsMeta } from '../redux/slices/othersDataSlice';
import { setSitePath, setTabOrPublicationPage } from '../redux/slices/appSlice';
import { setNews } from '../redux/slices/newsSlice';
import getSiteDataFunctions from './get_site_data_fns';
import { path_to_site } from './axios_settings';
import { handleNetworkError } from '../utils/networkErrorHandler';
import { safeJsonParse } from '../utils/safeJsonParse';

const normalizeText = (value) => {
  if (typeof value !== 'string') return '';
  const normalized = value.trim();
  if (!normalized || normalized.toLowerCase() === 'none') return '';
  return normalized;
};

const extractAuthorCardMeta = (entry) => {
  const parsedContent = Array.isArray(entry?.content)
    ? entry.content
    : safeJsonParse(entry?.content, []);
  const firstBlock = Array.isArray(parsedContent) ? parsedContent[0] : null;
  const blockContent = Array.isArray(firstBlock?.block_content)
    ? firstBlock.block_content
    : [];

  const imgBlock = blockContent.find((block) => {
    const type = (block?.cotent_type || block?.content_type || '').toLowerCase();
    return type === 'img' && normalizeText(block?.img_name);
  });

  const imgsBlock = blockContent.find((block) => {
    const type = (block?.cotent_type || block?.content_type || '').toLowerCase();
    return type === 'imgs_block' && normalizeText(block?.imgs_names);
  });

  const authorName =
    normalizeText(imgBlock?.content_text) ||
    normalizeText(imgsBlock?.content_text) ||
    normalizeText(entry?.show_name);

  let avatarName = normalizeText(imgBlock?.img_name);
  if (!avatarName && imgsBlock?.imgs_names) {
    try {
      const parsedImgs =
        typeof imgsBlock.imgs_names === 'string'
          ? JSON.parse(imgsBlock.imgs_names)
          : imgsBlock.imgs_names;
      if (Array.isArray(parsedImgs) && parsedImgs.length > 0) {
        avatarName = normalizeText(String(parsedImgs[0]));
      }
    } catch {
      avatarName = normalizeText(String(imgsBlock.imgs_names).split(',')[0]);
    }
  }

  if (avatarName.includes('\\')) {
    avatarName = avatarName.substring(avatarName.lastIndexOf('\\') + 1);
  }

  const avatarPath =
    avatarName && entry?.path && entry?.eng_name
      ? `https://${entry.path}${entry.eng_name}/${avatarName}`
      : '';

  return {
    id: entry?.id ?? null,
    eng_name: entry?.eng_name ?? '',
    author_name: authorName,
    avatar_path: avatarPath,
  };
};

const collectAuthorCardsMeta = (data) => {
  const tabs = Array.isArray(data?.tabs) ? data.tabs : [];
  const others = Array.isArray(data?.others) ? data.others : [];

  const fromTabs = tabs.flatMap((tab) =>
    Array.isArray(tab?.publications) ? tab.publications : [],
  );

  const candidates = [...fromTabs, ...others].filter((entry) => {
    const template = typeof entry?.template === 'string'
      ? safeJsonParse(entry.template, null)
      : entry?.template;
    const templateName = template?.template_name || (typeof template === 'string' ? template : '');
    return templateName === 'author_card';
  });

  const dedupedByEngName = new Map();
  candidates.forEach((entry) => {
    const meta = extractAuthorCardMeta(entry);
    if (!meta.eng_name && !meta.author_name && !meta.avatar_path) return;
    const key = meta.eng_name || String(meta.id || '');
    if (!key) return;
    if (!dedupedByEngName.has(key)) {
      dedupedByEngName.set(key, meta);
    }
  });

  return Array.from(dedupedByEngName.values());
};

const GetSiteData = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const dataVersion = useSelector((state) => state.app.dataVersion);
  const [localDataVersion, setLocalDataVersion] = React.useState(0);

  React.useEffect(() => {
    // console.log(
    //   'GetSiteData useEffect triggered, localDataVersion:',
    //   localDataVersion,
    //   'dataVersion:',
    //   dataVersion,
    // );
    // Проверяем, соответствует ли локальная версия глобальной
    if (localDataVersion !== dataVersion) {
      // console.log('Making API request to get site data...');
      const formData = new FormData();
      formData.append('request', 'get_site_data');

      axios
        .post(path_to_site, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(({ data }) => {
          // console.log('GetSiteData response:', data);
          // Используем функцию для поиска main_tab элемента
          const mainTabElement = getSiteDataFunctions.findMainTabElement(data);
          // Получаем путь из строки браузера
          const currentPath = location.pathname;
          // Используем функцию для обработки пути
          const { currentTabPath, currentPublicationPath } = getSiteDataFunctions.processPath(
            currentPath,
            data,
            mainTabElement,
          );
          //   console.log(currentTabPath, data.tabs);
          // console.log(currentPublicationPath, data.others);
          // Лог до записи в Redux: сырые данные, которые отправляем в setOthers.
          // console.log('[dispatch][setOthers][raw_payload_before_redux]', data?.others);
          // console.log(
          //   '[dispatch][setOthers][running_line_content_raw_before_redux]',
          //   Array.isArray(data?.others)
          //     ? data.others.filter((el) => el && el.tab_tag === 'running_line_content')
          //     : [],
          // );
          const authorCardsMeta = collectAuthorCardsMeta(data);
          dispatch(setTabs(data.tabs));
          dispatch(setNews(data.tabs));
          dispatch(setOthers(data.others));
          dispatch(setAuthorCardsMeta(authorCardsMeta));

          let fullPath = '';
          if (currentPublicationPath !== '') {
            fullPath = currentTabPath + '/' + currentPublicationPath;
            dispatch(setTabOrPublicationPage('publication'));
          } else {
            fullPath = currentTabPath;
            dispatch(setTabOrPublicationPage('tab'));
          }

          dispatch(setSitePath(fullPath));

          // Обновляем локальную версию после успешного запроса
          setLocalDataVersion(dataVersion);
        })
        .catch((error) => {
          // console.error('GetSiteData error:', {
          //   message: error?.message,
          //   status: error?.response?.status,
          //   statusText: error?.response?.statusText,
          //   data: error?.response?.data,
          // });
          // Обрабатываем сетевые ошибки (отсутствие интернета)
          if (handleNetworkError(error)) {
            return; // Игнорируем сетевые ошибки
          }

          // Для других ошибок можно добавить логирование, но не показывать пользователю
          // console.error('Error in GetSiteData:', error);
        });
    }
  }, [localDataVersion, dataVersion, dispatch, location, navigate]);

  return null;
};

export default GetSiteData;
